import React, { Component } from 'react';
import './VideoPlayer.css';

/**
 * VideoPlayer
 * The video player for the backpack. Uses a simple HTML5 <video> element, with
 * HTML controls that call actions on the <video> object. Also, ties into event handlers
 * for the <video> to update its' internal state to match the state of the video.
 *
 * @class VideoPlayer
 * @extends {Component}
 */

class VideoPlayer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      paused: true,
      muted: false,
      volume: 1.0,
      currentTime: 0,
      duration: 0
    };
  }

  /**
   * Toggle the player state of the video. If the video is already playing, pause it.
   * Otherwise, play it.
   *
   * @memberof VideoPlayer
   */
  playPause = () => {
    let { paused } = this.video;
    if (paused) {
      this.video.play();
    } else {
      this.video.pause();
    }
  }

  /**
   * Toggle the mute state of hte video. If the video is muted, unmute it. Otherwise, mute it.
   *
   * @memberof VideoPlayer
   */
  mute = () => {
    let { muted } = this.video;
    if (muted === false) {
      this.video.muted = true;
    } else {
      this.video.muted = false;
    }
  }

  /**
   * Event handler for changing the seek slider of the video player. Take the given event and
   * grab the destination value, and convert that to a timestamp of the video in seconds. Then,
   * seek the video to the destination time.
   *
   * @memberof VideoPlayer
   */
  seek = (ev) => {
    // Calculate the time in the video to seek to (get the percentage of the progress bar
    // seeked, and multiplay it by the duration of the video in seconds).
    let percentage = ev.target.value;
    let newTime = this.video.duration * percentage / 100;

    // Update the video time.
    this.video.currentTime = newTime;
    this.setState({ currentTime: newTime });
  }

  /**
   * Intercept mousedown on the seek bar. Used to temporarily pause the video if playing to allow
   * for seeking.
   *
   * @memberof VideoPlayer
   */
  seekMouseDown = (ev) => {
    this.video.pause();
  }

  /**
   * Intercept mouseup on the seek bar. Used to replay the video if it was playing before seeking
   * started.
   *
   * @memberof VideoPlayer
   */
  seekMouseUp = (ev) => {
    this.video.play();
  }

  /**
   * Event handler for changing the volume slider of the video player. Take the given event and
   * grab the destination value, and then set it to the player volume.
   *
   * @memberof VideoPlayer
   */
  changeVolume = (ev) => {
    let { value } = ev.target;
    this.video.volume = value;
  }

  /**
   * Event handler for the fullscreen button. Requests fullscreen access for the video.
   *
   * @memberof VideoPlayer
   */
  fullscreen = () => {
    const { video } = this;

    if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen();
    }
  }

  /**
   * = = = = = = = = = = = =
   * <video> Event Handlers
   * Used to update the internal `state` of this component with the current values for
   * video.paused, video.muted, video.volume, video.currentTime, and video.duration.
   * = = = = = = = = = = = =
   */

  /**
   * Called onPlay of the <video>.
   * Updates the `paused` of `this.state`.
   *
   * @memberof VideoPlayer
   */
  onVideoPlay = () => {
    this.setState({ paused: false });
  }

  /**
   * Called onPause of the <video>.
   * Updates the `paused` of `this.state`.
   *
   * @memberof VideoPlayer
   */
  onVideoPause = () => {
    this.setState({ paused: true });
  }

  /**
   * Called onVolumeChange of the <video>.
   * Updates the `muted` and `volume` of `this.state`.
   *
   * @memberof VideoPlayer
   */
  onVideoVolumeChange = () => {
    const { muted, volume } = this.video;
    this.setState({ muted, volume });
  }

  /**
   * Called onTimeUpdate of the <video>.
   * Updates the `currentTime` and `duration` of `this.state`.
   *
   * @memberof VideoPlayer
   */
  onVideoProgressChange = () => {
    const { currentTime, duration } = this.video;
    this.setState({ currentTime, duration });
  }

  /**
   * Render the video player. Composes the template with proper event handlers,
   * as well as renders out the current timestamp for the video.
   *
   * @returns
   * @memberof VideoPlayer
   */
  render() {
    const { state, props } = this;
    const { paused, muted, volume, duration, currentTime } = state;
    const { videoUrl } = props;

    let percentage = 0;
    if (currentTime && duration) {
      percentage = currentTime / duration;
    }

    let minutes = `${Math.floor(currentTime / 60)}`;
    let seconds = `${Math.floor(currentTime) - (minutes * 60)}`;

    // Pad the minutes number to be at least be two digits.
    let pad = "00";
    let secondsPadded = pad.substring(0, pad.length - seconds.length) + seconds;

    // Then, format as a timestamp
    let timestamp = `${minutes}:${secondsPadded}`;

    return (
      <div className="VideoPlayer">
        <div className="VideoPlayer__video-container">
          <video className="VideoPlayer__video"
            onPlay={this.onVideoPlay}
            onPause={this.onVideoPause}
            onVolumeChange={this.onVideoVolumeChange}
            onTimeUpdate={this.onVideoProgressChange}
            ref={(input) => { this.video = input; }}>
            <source src={videoUrl} type="video/mp4" />
          </video>

          <section className="VideoPlayer__controls">
            <button className="VideoPlayer__control-button"
              onClick={this.playPause}
              type="button"
              id="play-pause">
              {paused ? <svg width="12" height="14" viewBox="0 0 12 14" xmlns="http://www.w3.org/2000/svg"><path d="M12 7L0 14V0z" fill="#FFF" fillRule="evenodd"/></svg> : <svg width="12" height="14" viewBox="0 0 12 14" xmlns="http://www.w3.org/2000/svg"><path d="M0 0h4v14H0V0zm8 0h4v14H8V0z" fill="#FFF" fillRule="evenodd"/></svg>}
            </button>

            <input className="VideoPlayer__control-range VideoPlayer__seek-bar"
              onChange={this.seek}
              onMouseDown={this.seekMouseDown}
              onMouseUp={this.seekMouseUp}
              type="range"
              value={percentage * 100} />

            <div className="VideoPlayer__timestamp">{timestamp}</div>

            {/* <button onClick={this.mute} type="button" id="mute">{muted ? 'Unmute': 'Mute'}</button>
            <input type="range"
              id="volume-bar"
              min="0"
              max="1"
              step="0.1"
              onChange={this.changeVolume}
              value={volume} /> */}

            <button className="VideoPlayer__control-button VideoPlayer__full-screen"
              onClick={this.fullscreen}
              type="button"
              id="full-screen">
                <svg width="12" height="12" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg"><path d="M8 0h4v4h-2V2H8V0zM4 0H0v4h2V2h2V0zm0 10H2V8H0v4h12V8h-2v2H8v2H4v-2z" fill="#FFF" fillRule="evenodd"/></svg>
            </button>
          </section>
        </div>
      </div>
    );
  }
}


export default VideoPlayer;



// WEBPACK FOOTER //
// ./src/components/VideoPlayer.js